import {
  ExternalBranchesService,
  SyncExternalBranchJobsService,
  type PutExternalBranchRequestAdmin
} from '@/backend/market'
import { ExternalBranchesRepository } from '../../domain/repositories/ExternalBranchesRepository'
import { ExternalBranchesModel } from '../models/ExternalBranchesModel'
import type { ExternalBranchesEntity } from '../../domain/entities/ExternalBranchesEntity'
import type { CreateUpdateExternalBranchesEntity } from '../../domain/entities/CreateUpdateExternalBranchesEntity'
import { CreateUpdateExternalBranchesModel } from '../models/CreateUpdateExternalBranchesModel'
import type { ExternalBranchesFilterEntity } from '../../domain/entities/ExternalBranchesFilterEntity'
import { ExternalBranchesFilterModel } from '../models/ExternalBranchesFilterModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { CheckExternalBranchApiModel } from '../models/CheckExternalBranchApiModel'
import type { CheckExternalBranchApiEntity } from '../../domain/entities/CheckExternalBranchApiEntity'
import type { ExternalBranchJobFilterEntity } from '../../domain/entities/ExternalBranchJobFilterEntity'
import { ExternalBranchJobFilterModel } from '../models/ExternalBranchJobFilterModel'
import { ExternalBranchJobModel } from '../models/ExternalBranchJobModel'
import type { ExternalBranchJobEntity } from '../../domain/entities/ExternalBranchJobEntity'
import { ExternalBranchJobStatusLogModel } from '../models/ExternalBranchJobStatusLogModel'
import type { ExternalBranchJobStatusLogEntity } from '../../domain/entities/ExternalBranchJobStatusLogEntity'
export class ExternalBranchesRepositoryImpl implements ExternalBranchesRepository {
  async getExternalBranches(
    filterData: ExternalBranchesFilterEntity
  ): Promise<PaginateEntity<ExternalBranchesEntity[]> | { error: true }> {
    const externalBranchesFilterModel = new ExternalBranchesFilterModel(filterData)
    try {
      const response = await ExternalBranchesService.getExternalBranchesAsyncAdmin(
        externalBranchesFilterModel
      )
      const externalBranchModels = response.items?.map((item) => new ExternalBranchesModel(item))
      const externalBranchResponse = new PaginateEntity<ExternalBranchesModel[]>(
        response,
        externalBranchModels
      )

      return externalBranchResponse ?? []
    } catch {
      return { error: true }
    }
  }

  async getExternalBranch(id: number): Promise<ExternalBranchesEntity | { error: true }> {
    try {
      const response = await ExternalBranchesService.getExternalBranchByIdAsyncAdmin({
        id
      })
      const externalBranchModel = new ExternalBranchesModel(response)
      return externalBranchModel
    } catch {
      return { error: true }
    }
  }

  async createExternalBranch(
    data: CreateUpdateExternalBranchesEntity
  ): Promise<{ success: true } | { error: true }> {
    const externalBranchModel = new CreateUpdateExternalBranchesModel(data)
    try {
      await ExternalBranchesService.createExternalBranchAsyncAdmin({
        requestBody: externalBranchModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateExternalBranch(
    id: number,
    data: CreateUpdateExternalBranchesEntity
  ): Promise<{ success: true } | { error: true }> {
    const externalBranchModel = new CreateUpdateExternalBranchesModel(data)
    try {
      await ExternalBranchesService.updateExternalBranchAsyncAdmin({
        id,
        requestBody: externalBranchModel as PutExternalBranchRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteExternalBranch(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ExternalBranchesService.deleteExternalBranchAsyncAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async checkExternalBranchItems(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ExternalBranchesService.postCheckExternalBranchItemsAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async checkExternalBranchApi(
    id: number
  ): Promise<CheckExternalBranchApiEntity | { error: true }> {
    try {
      const response = await ExternalBranchesService.postCheckExternalBranchItemsAdmin({ id })
      const checkExternalBranchApiModel = new CheckExternalBranchApiModel(response)
      return checkExternalBranchApiModel
    } catch {
      return { error: true }
    }
  }
  async getExternalBranchJobs(
    filterData: ExternalBranchJobFilterEntity
  ): Promise<PaginateEntity<ExternalBranchJobEntity[]> | { error: true }> {
    try {
      const externalBranchJobFilterModel = new ExternalBranchJobFilterModel(filterData)
      const response = await SyncExternalBranchJobsService.getSyncExternalBranchJobsAsyncAdmin(
        externalBranchJobFilterModel
      )
      const externalBranchJobsModels = response.items?.map(
        (item) => new ExternalBranchJobModel(item)
      )
      const externalBranchJobsResponse = new PaginateEntity<ExternalBranchJobModel[]>(
        response,
        externalBranchJobsModels
      )
      return externalBranchJobsResponse ?? []
    } catch {
      return { error: true }
    }
  }
  async getExternalBranchJobStatusLogs(
    id: string
  ): Promise<ExternalBranchJobStatusLogEntity[] | { error: true }> {
    try {
      const response =
        await SyncExternalBranchJobsService.getSyncExternalBranchJobStatusLogsAsyncAdmin({ id })
      const externalBranchJobStatusLogsModels = response.logs?.map(
        (item) => new ExternalBranchJobStatusLogModel(item)
      )

      return externalBranchJobStatusLogsModels ?? []
    } catch {
      return { error: true }
    }
  }
  async syncExternalBranchJobs(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await SyncExternalBranchJobsService.postSyncExternalBranchJobAsyncAdmin({
        requestBody: {
          externalBranchId: id
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async cancelSyncExternalBranchJobs(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await SyncExternalBranchJobsService.cancelSyncExternalBranchJobAsyncAdmin({
        id
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
